import React, { useState, useEffect } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import alowedEmail from './alowedEmail.json';

import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import { required, bookingDatesRequired, composeValidators } from '../../../util/validators';
import {
  START_DATE,
  END_DATE,
  getStartOf,
  addTime,
  isSameDay,
  isDateSameOrAfter,
  isInRange,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
  monthIdString,
  initialVisibleMonth,
  timestampToDate,
} from '../../../util/dates';
import { LINE_ITEM_DAY, LINE_ITEM_NIGHT, TIME_SLOT_TIME, propTypes } from '../../../util/types';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';

import { Form, IconArrowHead, PrimaryButton, FieldDateRangeInput, H6 } from '../..';

import LoginNotificationBanner from '../../LoginNotificationBanner/LoginNotificationBanner';

import EmailVerificationBanner from '../../EmailVerificationBanner/EmailVerificationBanner';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';

import css from './BookingDatesForm.module.css';
import FieldDateAndTimeInput, { getAllTimeValues } from '../BookingTimeForm/FieldDateAndTimeInput';
// import BuiltInLabelDateRangePicker from '../../FieldDateRangeInput/BuiltInLabelDateRangePicker';
import FieldOnlyTimeHours from '../BookingTimeForm/FieldOnlyTimeHours';
import moment from 'moment';
import getUnixTimestamp from '../../../util/getUnixTimestamp';
import basedGetUrlRequestLogedIn from '../../../util/apiRequests/basedGetUrlRequestLogedIn';
import DayCountAvailableForBooking from '../BookingTimeForm/DayCountAvailableForBooking';
import NewDatePicker from './NewDatePicker';
import ErrorBanner from './ErrorBanner';
import UnattendedGearIsNotCovered from '../../../containers/TransactionPage/TransactionPanel/UnattendedGearIsNotCovered';
import PollingComponent from '../../../containers/ContactDetailsPage/ContactDetailsForm/PollingComponent';
import basedPostUrlRequestLogedIn from '../../../util/apiRequests/basedPostUrlRequestLogedIn';
import ShippingToggle from '../../../containers/TransactionPage/TransactionPanel/ShippingToggle/ShippingToggle';

const TODAY = new Date();

const nextMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, offset, 'months');
const prevMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, -1 * offset, 'months');
const endOfRange = (date, dayCountAvailableForBooking, timeZone) =>
  getStartOf(date, 'day', timeZone, dayCountAvailableForBooking - 1, 'days');

/**
 * Return a boolean indicating if given date can be found in an array
 * of tile slots (start dates).
 */
const timeSlotsContain = (timeSlots, date, timeZone) => {
  const foundIndex = timeSlots.findIndex(slot =>
    isInRange(date, slot.attributes.start, slot.attributes.end, 'hour', timeZone)
  );
  return foundIndex > -1;
};

const pickMonthlyTimeSlots = (monthlyTimeSlots, date, timeZone) => {
  const monthId = monthIdString(date, timeZone);
  return monthlyTimeSlots?.[monthId]?.timeSlots || [];
};

/**
 * Find first blocked date between two dates.
 * If none is found, null is returned.
 *
 * @param {Object} monthlyTimeSlots propTypes.timeSlot objects
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 * @param {String} timeZone time zone id
 */
const firstBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const firstDate = getStartOf(startDate, 'day', timeZone, 1, 'days');
  if (isSameDay(firstDate, endDate, timeZone)) {
    return null;
  }

  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, firstDate, timeZone);
  const contains = timeSlotsContain(timeSlots, firstDate, timeZone);
  return contains ? firstBlockedBetween(monthlyTimeSlots, firstDate, endDate, timeZone) : firstDate;
};

/**
 * Find last blocked date between two dates.
 * If none is found, null is returned.
 *
 * @param {Array} timeSlots propTypes.timeSlot objects
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 * @param {String} timeZone time zone id
 */
const lastBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const previousDate = getStartOf(endDate, 'day', timeZone, -1, 'days');
  if (isSameDay(previousDate, startDate, timeZone)) {
    return null;
  }

  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, previousDate, timeZone);
  return timeSlotsContain(timeSlots, previousDate, timeZone)
    ? lastBlockedBetween(monthlyTimeSlots, startDate, previousDate, timeZone)
    : previousDate;
};

/**
 * Check if a blocked date can be found between two dates.
 *
 * @param {Object} timeSlots propTypes.timeSlot objects
 * @param {String} timeZone time zone id
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 */
const isBlockedBetween = (monthlyTimeSlots, timeZone) => (startDate, endDate) => {
  const startInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(startDate, timeZone),
    'day',
    timeZone
  );
  const endInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(endDate, timeZone),
    'day',
    timeZone
  );
  return !!firstBlockedBetween(monthlyTimeSlots, startInListingTZ, endInListingTZ, timeZone);
};

const isStartDateSelected = (timeSlots, startDate, endDate, focusedInput) => {
  return (
    timeSlots && startDate && (!endDate || focusedInput === END_DATE) && focusedInput !== START_DATE
  );
};

const endDateToPickerDate = (unitType, endDate, timeZone) => {
  const isValid = endDate instanceof Date;
  const isDaily = unitType === LINE_ITEM_DAY;

  if (!isValid) {
    return null;
  } else if (isDaily) {
    // API end dates are exlusive, so we need to shift them with daily
    // booking.
    return getStartOf(endDate, 'day', timeZone, -1, 'days');
  } else {
    return endDate;
  }
};

/**
 * Returns an isOutsideRange function that can be passed to
 * a react-dates DateRangePicker component.
 */
const isOutsideRangeFn = (
  monthlyTimeSlots,
  startDate,
  endDate,
  lineItemUnitType,
  dayCountAvailableForBooking,
  timeZone
) => focusedInput => {
  const endOfAvailableRange = dayCountAvailableForBooking - 1;
  const endOfAvailableRangeDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange, 'days');
  const outOfBookableDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange + 1, 'days');
  const startOfStartDay = getStartOf(startDate, 'day', timeZone);

  // start date selected, end date missing
  const startDateSelected = isStartDateSelected(monthlyTimeSlots, startDate, endDate, focusedInput);
  const endOfBookableRange = startDateSelected
    ? firstBlockedBetween(monthlyTimeSlots, startDate, outOfBookableDate, timeZone)
    : null;

  if (endOfBookableRange) {
    // end the range so that the booking can end at latest on
    // nightly booking: the day the next booking starts
    // daily booking: the day before the next booking starts
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const lastDayToEndBooking = endDateToPickerDate(
        lineItemUnitType,
        endOfBookableRange,
        timeZone
      );
      return (
        !isDateSameOrAfter(dayInListingTZ, startOfStartDay) ||
        !isDateSameOrAfter(lastDayToEndBooking, dayInListingTZ)
      );
    };
  }

  // end date selected, start date missing
  // -> limit the earliest start date for the booking so that it
  // needs to be after the previous booked date
  const endDateSelected = monthlyTimeSlots && endDate && !startDate && focusedInput !== END_DATE;
  const previousBookedDate = endDateSelected
    ? lastBlockedBetween(monthlyTimeSlots, TODAY, endDate, timeZone)
    : null;

  if (previousBookedDate) {
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const firstDayToStartBooking = getStartOf(previousBookedDate, 'day', timeZone, 1, 'days');
      return (
        !isDateSameOrAfter(dayInListingTZ, firstDayToStartBooking) ||
        !isDateSameOrAfter(endOfAvailableRangeDate, dayInListingTZ)
      );
    };
  }

  // standard isOutsideRange function
  return day => {
    const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
    const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
    return (
      !isDateSameOrAfter(dayInListingTZ, TODAY) ||
      !isDateSameOrAfter(endOfAvailableRangeDate, dayInListingTZ)
    );
  };
};

// Checks if time slot (propTypes.timeSlot) start time equals a day (moment)
const timeSlotEqualsDay = (timeSlot, day, timeZone) => {
  const isTimeBased = timeSlot?.attributes?.type === TIME_SLOT_TIME;
  return isTimeBased
    ? isInRange(day, timeSlot.attributes.start, timeSlot.attributes.end, undefined, timeZone)
    : false;
};

/**
 * Returns an isDayBlocked function that can be passed to
 * a react-dates DateRangePicker component.
 */
const isDayBlockedFn = (
  monthlyTimeSlots,
  startDate,
  endDate,
  unitType,
  dayCountAvailableForBooking,
  timeZone
) => focusedInput => {
  const endOfAvailableRange = dayCountAvailableForBooking - 1;
  const outOfBookableDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange + 1, 'days');
  const timeSlotsOnSelectedMonth = pickMonthlyTimeSlots(monthlyTimeSlots, startDate, timeZone);

  // Start date selected/focused, end date missing
  const startDateSelected = isStartDateSelected(
    timeSlotsOnSelectedMonth,
    startDate,
    endDate,
    focusedInput
  );

  // Find the end of bookable range after a start date
  const endOfBookableRange = startDateSelected
    ? firstBlockedBetween(monthlyTimeSlots, startDate, outOfBookableDate, timeZone)
    : null;

  // end date is focused but no dates are selected
  const selectingEndDate =
    timeSlotsOnSelectedMonth &&
    !startDate &&
    !endDate &&
    focusedInput === END_DATE &&
    unitType === LINE_ITEM_NIGHT;

  if (selectingEndDate) {
    // if end date is being selected first, block the day after a booked date
    // (as a booking can end on the day the following booking starts)
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, dayInListingTZ, timeZone);
      return !timeSlots.find(timeSlot => timeSlotEqualsDay(timeSlot, dayInListingTZ));
    };
  } else if (endOfBookableRange || !timeSlotsOnSelectedMonth) {
    // a next bookable range is found or time slots are not provided
    // -> booking range handles blocking dates
    return () => false;
  } else {
    // otherwise return standard timeslots check
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, dayInListingTZ, timeZone);
      return !timeSlots.find(timeSlot => timeSlotEqualsDay(timeSlot, dayInListingTZ));
    };
  }
};

const fetchMonthData = (
  date,
  listingId,
  dayCountAvailableForBooking,
  timeZone,
  onFetchTimeSlots
) => {
  const endOfRangeDate = endOfRange(TODAY, dayCountAvailableForBooking, timeZone);

  // Don't fetch timeSlots for past months or too far in the future
  if (isInRange(date, TODAY, endOfRangeDate)) {
    // Use "today", if the first day of given month is in the past
    const start = isDateSameOrAfter(TODAY, date) ? TODAY : date;

    // Use endOfRangeDate, if the first day of the next month is too far in the future
    const nextMonthDate = nextMonthFn(date, timeZone);
    const end = isDateSameOrAfter(nextMonthDate, endOfRangeDate)
      ? getStartOf(endOfRangeDate, 'day', timeZone)
      : nextMonthDate;

    // Fetch time slots for given time range
    onFetchTimeSlots(listingId, start, end, timeZone);
  }
};

const handleMonthClick = (
  currentMonth,
  monthlyTimeSlots,
  fetchMonthData,
  dayCountAvailableForBooking,
  timeZone,
  listingId,
  onFetchTimeSlots
) => monthFn => {
  // Callback function after month has been updated.
  // react-dates component has next and previous months ready (but inivisible).
  // we try to populate those invisible months before user advances there.
  fetchMonthData(
    monthFn(currentMonth, timeZone, 2),
    listingId,
    dayCountAvailableForBooking,
    timeZone,
    onFetchTimeSlots
  );

  // If previous fetch for month data failed, try again.
  const monthId = monthIdString(currentMonth, timeZone);
  const currentMonthData = monthlyTimeSlots[monthId];
  if (currentMonthData && currentMonthData.fetchTimeSlotsError) {
    fetchMonthData(
      currentMonth,
      listingId,
      dayCountAvailableForBooking,
      timeZone,
      onFetchTimeSlots
    );
  }
};
const handleFormSubmit = (
  setFocusedInput,
  onSubmit,
  currentUser,
  handleCheckRequieredData
) => async e => {
  // Check if e is an event and has preventDefault method
  if (e && typeof e.preventDefault === 'function') {
    e.preventDefault();
  }

  const status = await handleCheckRequieredData();
  if (status) {
    return;
  }

  const { bookingStartTime, bookingEndTime } = e || {};
  const startDate = bookingStartTime ? timestampToDate(bookingStartTime) : null;
  const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : null;

  if (!startDate) {
    setFocusedInput(START_DATE);
  } else if (!endDate) {
    setFocusedInput(END_DATE);
  } else if (!currentUser || !currentUser.attributes?.emailVerified) {
    // Open the modal to indicate missing information
  } else {
    onSubmit(e);
  }
};
// Function that can be passed to nested components
// so that they can notify this component when the
// focused input changes.
const handleFocusedInputChange = setFocusedInput => focusedInput => {
  setFocusedInput(focusedInput);
};

// When the values of the form are updated we need to fetch
// lineItems from this Template's backend for the EstimatedTransactionMaybe
// In case you add more fields to the form, make sure you add
// the values here to the orderData object.
const handleFormSpyChange = (
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
  hasInsurance
) => formValues => {
  const { bookingStartTime, bookingEndTime } = formValues.values;
  const startDate = bookingStartTime ? timestampToDate(bookingStartTime) : null;
  const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : null;

  // Note: we expect values bookingStartTime and bookingEndTime to be strings
  // which is the default case when the value has been selected through the form
  const isStartBeforeEnd = bookingStartTime < bookingEndTime;
  const hasBookingLengthDiscount = true; //Always applies booking length discount
  const hasTaxes = true; //Always applies taxes
  // const hasInsurance = true; //Always applies insurance
  const isShipping = formValues?.values?.isShipping;
  // const discountToApplyNow = discountToApplyNow
  if (bookingStartTime && bookingEndTime && isStartBeforeEnd && !fetchLineItemsInProgress) {
    onFetchTransactionLineItems({
      orderData: {
        bookingStart: startDate,
        bookingEnd: endDate,
        hasBookingLengthDiscount,
        hasTaxes,
        hasInsurance,
        isShipping,
      },
      listingId,
      isOwnListing,
    });
  }
};

// IconArrowHead component might not be defined if exposed directly to the file.
// This component is called before IconArrowHead component in components/index.js
const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

const Next = props => {
  const { currentMonth, dayCountAvailableForBooking, timeZone } = props;
  const nextMonthDate = nextMonthFn(currentMonth, timeZone);

  return isDateSameOrAfter(
    nextMonthDate,
    endOfRange(TODAY, dayCountAvailableForBooking, timeZone)
  ) ? null : (
    <NextIcon />
  );
};
const Prev = props => {
  const { currentMonth, timeZone } = props;
  const prevMonthDate = prevMonthFn(currentMonth, timeZone);
  const currentMonthDate = getStartOf(TODAY, 'month', timeZone);

  return isDateSameOrAfter(prevMonthDate, currentMonthDate) ? <PrevIcon /> : null;
};
const handleCheckStatus = async () => {
  return await basedPostUrlRequestLogedIn('/api/check-status/billing-address', {});
};

export const BookingDatesFormComponent = props => {
  const [isDataUnregistered, setIsDataUnregistered] = useState(false);
  const handleCheckRequieredData = async () => {
    const res = await handleCheckStatus();
    if (res.status === true) {
      setIsDataUnregistered(true);
      return res.status;
    } else {
      setIsDataUnregistered(false);
      return res.status;
    }
  };
  const [focusedInput, setFocusedInput] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(getStartOf(TODAY, 'month', props.timeZone));

  useEffect(() => {
    // Call onMonthChanged function if it has been passed in among props.
    if (props.onMonthChanged) {
      props.onMonthChanged(monthId);
    }
  }, [currentMonth]);
  // const listingId = props.listingId;

  const [estimatedValue, setEstimatedValue] = useState(0);
  useEffect(() => {
    try {
      basedGetUrlRequestLogedIn('/api/get-listing-data/' + props.listingId.uuid).then(response => {
        if (response?.attributes?.publicData?.estimatedValue?.amount) {
          setEstimatedValue(
            parseFloat(response?.attributes?.publicData?.estimatedValue?.amount) / 100
          );
        }
      });
    } catch (error) {
      console.log('error:', error);
    }
  }, [props.listingId]);
  const hasInsurance = estimatedValue >= 80 ? true : false; //Always applies insurance

  const {
    rootClassName,
    className,
    price: unitPrice,
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    onSubmit,
    timeZone,
    dayCountAvailableForBooking,
    marketplaceName,
    currentUser,
    listing,
    ...rest
  } = props;
  const { isShippingActive, shippingOption, weight, shippingPrice } =
    listing.attributes.publicData.shippingData || {};
  const availableShippingOption =
    shippingOption === 'oversized' && shippingPrice
      ? true
      : shippingOption === 'normal' && weight
      ? true
      : false;
  const classes = classNames(rootClassName || css.root, className);
  const onFormSubmit = handleFormSubmit(
    setFocusedInput,
    onSubmit,
    currentUser,
    handleCheckRequieredData
  );
  const onFocusedInputChange = handleFocusedInputChange(setFocusedInput);
  const onFormSpyChange = handleFormSpyChange(
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    hasInsurance
  );
  return (
    <>
      <FinalForm
        {...rest}
        unitPrice={unitPrice}
        onSubmit={onFormSubmit}
        render={fieldRenderProps => {
          const {
            endDatePlaceholder,
            startDatePlaceholder,
            form,
            formId,
            handleSubmit,
            intl,
            lineItemUnitType,
            values,
            monthlyTimeSlots,
            lineItems,
            fetchLineItemsError,
            onFetchTimeSlots,
          } = fieldRenderProps;

          const isShipping = values?.isShipping;
          const startTime = values && values.bookingStartTime ? values.bookingStartTime : null;
          const endTime = values && values.bookingEndTime ? values.bookingEndTime : null;
          const startDate = startTime ? timestampToDate(startTime) : null;
          const endDate = endTime ? timestampToDate(endTime) : null;
          const startDateErrorMessage = intl.formatMessage({
            id: 'FieldDateRangeInput.invalidStartDate',
          });
          const endDateErrorMessage = intl.formatMessage({
            id: 'FieldDateRangeInput.invalidEndDate',
          });
          const [unattendedGear, setUnattendedGear] = useState(false);
          const handleUnattendedGear = () => {
            setUnattendedGear(!unattendedGear);
          };
          const unattendedGearLogic = hasInsurance === false ? true : unattendedGear;

          // This is the place to collect breakdown estimation data.
          // Note: lineItems are calculated and fetched from this Template's backend
          // so we need to pass only booking data that is needed otherwise
          // If you have added new fields to the form that will affect to pricing,
          // you need to add the values to handleOnChange function
          const breakdownData =
            startDate && endDate
              ? {
                  startDate,
                  endDate,
                }
              : null;

          const showEstimatedBreakdown =
            breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

          const dateFormatOptions = {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          };

          const startOfToday = getStartOf(TODAY, 'day', timeZone);
          const tomorrow = addTime(startOfToday, 1, 'days');
          const startDatePlaceholderText =
            startDatePlaceholder || intl.formatDate(startOfToday, dateFormatOptions);
          const endDatePlaceholderText =
            endDatePlaceholder || intl.formatDate(tomorrow, dateFormatOptions);

          const onMonthClick = handleMonthClick(
            currentMonth,
            monthlyTimeSlots,
            fetchMonthData,
            dayCountAvailableForBooking,
            timeZone,
            listingId,
            onFetchTimeSlots
          );
          const isDayBlocked = isDayBlockedFn(
            monthlyTimeSlots,
            startDate,
            endDate,
            lineItemUnitType,
            dayCountAvailableForBooking,
            timeZone
          );
          const isOutsideRange = isOutsideRangeFn(
            monthlyTimeSlots,
            startDate,
            endDate,
            lineItemUnitType,
            dayCountAvailableForBooking,
            timeZone
          );
          const showDatesTest = true;
          const FieldDateAndTimeInputTest = false;
          const customer = currentUser;
          const phone = customer?.attributes?.profile?.protectedData?.phoneNumber;
          const customerNationalIdNumber =
            customer?.attributes?.profile?.privateData?.siltUserData?.nationalIdNumber;
          const customerPersonalNumber =
            customer?.attributes?.profile?.privateData?.siltUserData?.personalNumbers;
          const customerPassportNumber =
            customer?.attributes?.profile?.privateData?.siltUserData?.passportNumber;

          const customer_identificationNumber = customerPersonalNumber
            ? customerPersonalNumber
            : customerPassportNumber
            ? customerPassportNumber
            : customerNationalIdNumber
            ? customerNationalIdNumber
            : undefined;

          const protectedData = customer?.attributes?.profile?.protectedData;
          const customerBillingAddress = protectedData?.billingAddress;
          const customerBuilding = protectedData?.building;
          const customerBillingAddressSelectedPlace = customerBillingAddress?.selectedPlace;
          // const customerAddressContext = customerBillingAddressSelectedPlace?.context;
          const customerAddressStreet = customerBillingAddressSelectedPlace?.address;
          const customer_address = customerBuilding + ' ' + customerAddressStreet;
          const { address } = customerBillingAddress || {};
          const {
            street,
            street_number,
            city,
            postal_code,
            country,
            state,
            door_number,
            door_name,
          } = address || {};
          const allCustomerData = [
            { name: 'street', value: street },
            { name: 'street_number', value: street_number },
            { name: 'city', value: city },
            { name: 'postal_code', value: postal_code },
            { name: 'country', value: country },
            { name: 'state', value: state },
            { name: 'phone', value: phone },
          ];

          const allCustomerDataDefined = allCustomerData.every(({ name, value }) => {
            if (value == null || value === undefined) {
              return false; // Return false if any value is undefined or null
            }
            return true; // Return true if the value is defined
          });

          const undefinedData = allCustomerData
            .filter(({ value }) => value == null || value === undefined)
            .map(({ name }) => ({
              name,
            }));

          const [showBookingHours, setShowBookingHours] = useState(true);

          function checkConditions(allCustomerDataDefined, currentUser, unattendedGearLogic) {
            if (allCustomerDataDefined === false) {
              return true;
            }
            if (currentUser == null || currentUser == undefined) {
              return true;
            }
            if (!unattendedGearLogic) {
              return true;
            }
            if (!currentUser.attributes.emailVerified) {
              return true;
            }
            if (typeof currentUser.attributes.profile.metadata.siltUserId !== 'string') {
              return true;
            }  // Todo: Deprecate siltUserStatus.
            if (currentUser.attributes.profile.metadata.siltUserStatus !== 'SUCCESS') {
              return true;
            }
            return false;
          }

          checkConditions(allCustomerDataDefined, currentUser, unattendedGearLogic);
          return (
            <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
              <FormSpy subscription={{ values: true }} onChange={onFormSpyChange} />
              <div className={css.containerDateSelect}>
                <NewDatePicker
                  {...props}
                  form={form}
                  {...fieldRenderProps}
                  monthlyTimeSlots={monthlyTimeSlots}
                />

                <DayCountAvailableForBooking
                  startDateInputProps={{
                    label: intl.formatMessage({ id: 'BookingDatesForm.bookingStartTitle' }),
                    placeholderText: startDatePlaceholderText,
                  }}
                  endDateInputProps={{
                    label: intl.formatMessage({ id: 'BookingDatesForm.bookingEndTitle' }),
                    placeholderText: endDatePlaceholderText,
                  }}
                  className={css.bookingDates}
                  listingId={listingId}
                  onFetchTimeSlots={onFetchTimeSlots}
                  monthlyTimeSlots={monthlyTimeSlots}
                  values={values}
                  intl={intl}
                  form={form}
                  timeZone={timeZone}
                  dayCountAvailableForBooking={dayCountAvailableForBooking}
                  {...props}
                  dateValues={breakdownData}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0px' }}>
                  <p style={{ fontSize: '8px', margin: '0px' }}>
                    {intl.formatMessage({ id: 'BookingForm.note' })}
                  </p>
                  <p style={{ fontSize: '8px', margin: '0px' }}>
                    {intl.formatMessage({ id: 'BookingForm.note' })}
                  </p>
                </div>
                {/* {showBookingHours && (
                  <FieldOnlyTimeHours
                    startDateInputProps={{
                      label: intl.formatMessage({ id: 'BookingDatesForm.bookingStartTitle' }),
                      placeholderText: startDatePlaceholderText,
                    }}
                    endDateInputProps={{
                      label: intl.formatMessage({ id: 'BookingDatesForm.bookingEndTitle' }),
                      placeholderText: endDatePlaceholderText,
                    }}
                    className={css.bookingDates}
                    listingId={listingId}
                    onFetchTimeSlots={onFetchTimeSlots}
                    monthlyTimeSlots={monthlyTimeSlots}
                    values={values}
                    intl={intl}
                    form={form}
                    timeZone={timeZone}
                    dayCountAvailableForBooking={dayCountAvailableForBooking}
                  />
                )} */}
              </div>
              {showEstimatedBreakdown ? (
                <div className={css.priceBreakdownContainer}>
                  <H6 as="h3" className={css.bookingBreakdownTitle}>
                    <FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />
                  </H6>
                  <hr className={css.totalDivider} />
                  <EstimatedCustomerBreakdownMaybe
                    breakdownData={breakdownData}
                    lineItems={lineItems}
                    timeZone={timeZone}
                    currency={unitPrice.currency}
                    marketplaceName={marketplaceName}
                    processName={BOOKING_PROCESS_NAME}
                  />
                </div>
              ) : null}
              {currentUser != null && currentUser != undefined && fetchLineItemsError ? (
                <span className={css.sideBarError}>
                  <FormattedMessage id="BookingDatesForm.fetchLineItemsError" />
                </span>
              ) : null}
              {(currentUser == null || currentUser == undefined) && fetchLineItemsError ? (
                <span className={css.sideBarError}>
                  <FormattedMessage id="BookingDatesForm.notRegisteredError" />
                </span>
              ) : null}
              {isDataUnregistered && (
                <PollingComponent
                  intl={intl}
                  handleChangeStatus={value => {
                    setIsDataUnregistered(value);
                  }}
                />
              )}
              {/* {!allCustomerDataDefined && currentUser && (
                <ErrorBanner
                  message={intl.formatMessage({
                    id: `BookingDatesForm.${undefinedData?.[0]?.name}`,
                  })}
                  buttonText={intl.formatMessage({
                    id: `BookingDatesForm.editeError`,
                  })}
                  intl={intl}
                />
              )} */}
              <div className={css.container_padding_20px}>
                {showEstimatedBreakdown && isShippingActive && availableShippingOption && (
                  <ShippingToggle
                    initialState={isShipping}
                    listing={listing}
                    lineItems={lineItems}
                    form={form}
                    intl={intl}
                    startDate={startDate}
                    endDate={endDate}
                    monthlyTimeSlots={props?.monthlyTimeSlots}
                  />
                )}
                {showEstimatedBreakdown && hasInsurance && estimatedValue >= 80 && (
                  <UnattendedGearIsNotCovered
                    intl={intl}
                    handleUnattendedGear={handleUnattendedGear}
                  />
                )}
              </div>

              <div className={css.submitButton}>
                {(currentUser == null || false) && (
                  <PrimaryButton
                    type="submit"
                    onClick={e => {
                      const path = window.location.pathname;
                      const currentLang = path.split('/')[1];

                      e.preventDefault();
                      window.location.href = `/${currentLang}/signup`;
                    }}
                  >
                    <FormattedMessage id="BookingDatesForm.requestToBook" />
                  </PrimaryButton>
                )}

                {currentUser && (
                  <PrimaryButton
                    type="submit"
                    inProgress={fetchLineItemsInProgress}
                    disabled={
                      !currentUser ||
                      !unattendedGearLogic ||
                      !currentUser.attributes.emailVerified ||
                      (
                        currentUser.attributes.profile.metadata.userVerificationStatus !== 'Approved' &&
                        currentUser.attributes.profile.metadata.userVerificationStatus !== 'completed' &&
                        currentUser.attributes.profile.metadata.siltUserStatus !== 'SUCCESS'
                      )
                    }
                  >
                    <FormattedMessage id="BookingDatesForm.requestToBook" />
                  </PrimaryButton>
                )}
              </div>

              {currentUser == null ||
                currentUser == undefined ||
                (!currentUser.attributes.emailVerified && (
                  <p>
                    <EmailVerificationBanner />
                  </p>
                ))}

              {currentUser &&
                !(
                  currentUser.attributes.profile.metadata.userVerificationStatus === 'Approved' ||
                  currentUser.attributes.profile.metadata.userVerificationStatus === 'completed' ||
                  currentUser.attributes.profile.metadata.siltUserStatus === 'SUCCESS'
                ) && (
                  <p>
                    <LoginNotificationBanner />
                  </p>
                )}

              {/* {currentUser == null ||
                currentUser == undefined ||
                typeof currentUser.attributes.profile.metadata.siltUserId !== 'string' ||
                (currentUser.attributes.profile.metadata.siltUserStatus !== 'SUCCESS' && (
                  <p>
                    <LoginNotificationBanner />
                  </p>
                ))} */}
              <p className={css.finePrint}>
                <FormattedMessage
                  id={
                    isOwnListing
                      ? 'BookingDatesForm.ownListing'
                      : 'BookingDatesForm.youWontBeChargedInfo'
                  }
                />
              </p>
            </Form>
          );
        }}
      />
    </>
  );
};

BookingDatesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  lineItems: null,
  fetchLineItemsError: null,
  monthlyTimeSlots: null,
};

BookingDatesFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  lineItemUnitType: propTypes.lineItemUnitType.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,
  dayCountAvailableForBooking: number.isRequired,

  currentUser: propTypes.currentUser,
};

const BookingDatesForm = compose(injectIntl)(BookingDatesFormComponent);
BookingDatesForm.displayName = 'BookingDatesForm';

export default BookingDatesForm;
